import React, { useCallback, useEffect, useState } from 'react';

import { ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Drawer as DrawerAntD, Modal, Result, Select, Table, Tag, message } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import {
  canReview,
  canRevoke,
  inviteDeadline,
  inviteDirectionLabel,
  INVITE_STATUS_STYLE,
  inviteStatusLabel,
} from './inviteLabels';



import { deleteInvite, getInvites, revokeInvite } from '../../../../hooks/interoperability';
import {
  InviteDirection,
  InviteListItem,
  InviteQuery,
  InviteStatus,
} from '../../../../types/responses/Interoperability';
import StatusBadge from '../../../atoms/StatusBadge';
import { InviteDrawerDetails } from '../../../molecules/InteroperabilityDrawers';
import { ListSkeleton } from '../../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { PartnerLogo } from '../../../molecules/PartnerLogo';
import { directionLabel, directionOfInvite } from '../contractRules';
import { CellSub, Chevron, ClickableRows, FilterBar, FilterCount, FilterSpacer, ListCard } from '../styled';
import { formatCount } from '../transactionFormat';

const Partner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Deadline = styled.div`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Foot = styled.div`
  border-top: 1px solid #e9ebee;
  background: #fafbfc;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11.5px;
  color: #6b7280;
`;

const Note = styled.span`
  flex: 1;
`;

/** As acoes da linha em fila, sem quebrar a altura da tabela. */
/** O Drawer do antd nao declara children no typing desta versao. */
const Drawer: any = DrawerAntD;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  white-space: nowrap;
`;

const PAGE_SIZE = 25;

/** Um segundo, para o prazo do convite pendente andar sozinho na tela. */
const TICK_MS = 1000;

export type InvitesTabProps = {
  tenantId: string;

  /**
   * Revisar um convite recebido: abre o mesmo fluxo de aceite do banner.
   *
   * Recebe o tenant do parceiro, e não o id do convite, porque é por parceiro que a
   * URL do módulo recorta — e é assim que o shell reencontra o convite esperado.
   */
  onReview: (partnerTenantId: string) => void;

  /** Convite revogado: o shell recarrega banner e contadores. */
  onChanged: () => void;
};

/**
 * Aba Convites.
 *
 * O que existia antes eram o banner de recebidos pendentes e um contador de enviados.
 * Convite usado, expirado ou revogado não aparecia em lugar nenhum — depois de gerado,
 * o convite saía de vista e o rastro ficava só no Histórico, como evento solto.
 *
 * E revogar, que o backend oferece desde o B2, não tinha caminho pela interface.
 */
export const InvitesTab: React.FC<InvitesTabProps> = ({ tenantId, onReview, onChanged }) => {
  const [invites, setInvites] = useState<InviteListItem[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [query, setQuery] = useState<InviteQuery>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [detail, setDetail] = useState<InviteListItem | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);

  /**
   * Relógio da tela.
   *
   * O prazo do convite pendente é contado a partir do expires_at do servidor; este
   * estado só existe para redesenhar a célula a cada segundo. Sem ele o contador
   * ficaria congelado no valor do carregamento.
   */
  const [now, setNow] = useState(() => new Date());

  /*
    A query entra na dependência do useCallback como texto: objeto novo a cada render
    dispararia recarga infinita. É o mesmo recurso que Transações e Histórico já usam.
  */
  const queryKey = JSON.stringify(query);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);

      const parsed: InviteQuery = JSON.parse(queryKey);
      const result = await getInvites(tenantId, page, PAGE_SIZE, parsed);

      setInvites(result?.content || []);
      setTotalElements(result?.totalElements || 0);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), TICK_MS);

    return () => clearInterval(timer);
  }, []);

  /**
   * Revogar, com confirmação.
   *
   * Destrutivo e irreversível: o token deixa de valer e reconectar exige gerar outro
   * convite. A confirmação diz o efeito, e não "tem certeza?".
   */
  const confirmRevoke = (invite: InviteListItem) => {
    Modal.confirm({
      title: T('Revoke this invite?'),
      icon: <ExclamationCircleOutlined />,
      content: `${TF('The token stops working immediately and')} ${
        invite.partner_name || TF('the partner')
      } ${TF('will no longer be able to accept it. To connect later you have to send a new invite.')}`,
      okText: T('Revoke'),
      okButtonProps: { danger: true },
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          setBusyId(invite.invite_id);
          await revokeInvite(tenantId, invite.invite_id);
          message.success(T('Invite revoked.'));
          onChanged();
          await load();
        } catch {
          message.error(T('Could not revoke the invite. Nothing was changed.'));
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  /**
   * Excluir apaga de vez o convite. So aparece quando ja esta revogado, e a confirmacao deixa
   * claro que e permanente — o backend recusa se nao estiver revogado.
   */
  const confirmDelete = (invite: InviteListItem) => {
    Modal.confirm({
      title: T('Delete this revoked invite?'),
      icon: <ExclamationCircleOutlined />,
      content: T(
        'The invite is removed for good. This cannot be undone; the audit trail keeps the record.',
      ),
      okText: T('Delete'),
      okButtonProps: { danger: true },
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          setBusyId(invite.invite_id);
          await deleteInvite(tenantId, invite.invite_id);
          setDetail(null);
          message.success(T('Invite deleted.'));
          onChanged();
          await load();
        } catch {
          message.error(T('Could not delete the invite. Nothing was changed.'));
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  /** Trocar filtro volta para a primeira página: a atual pode não existir no resultado. */
  const changeQuery = (change: InviteQuery) => {
    setQuery((current) => {
      const next = { ...current, ...change };

      // Chave ausente e não vazia: o servidor entende ausência como "qualquer".
      (Object.keys(next) as (keyof InviteQuery)[]).forEach((key) => {
        if (!next[key]) {
          delete next[key];
        }
      });

      return next;
    });
    setPage(0);
  };

  const columns = [
    {
      title: T('Partner'),
      key: 'partner',
      render: (_: unknown, invite: InviteListItem) => (
        <Partner>
          <PartnerLogo name={invite.partner_name || ''} alias={invite.partner_alias} />
          <div>
            {invite.partner_name || '—'}
            {invite.scope_account_count ? (
              <CellSub>
                {formatCount(invite.scope_account_count)} {T('account(s)')}
                {invite.inherits_new_subaccounts ? ` · ${T('inherits new ones')}` : ''}
              </CellSub>
            ) : null}
          </div>
        </Partner>
      ),
    },
    {
      title: T('Direction'),
      key: 'direction',
      render: (_: unknown, invite: InviteListItem) => inviteDirectionLabel(invite.direction),
    },
    {
      title: T('Contract'),
      key: 'contract',
      render: (_: unknown, invite: InviteListItem) =>
        // A seta sai da perspectiva de quem olha: o mesmo convite unilateral me faz
        // compartilhar quando eu enviei, e receber quando eu recebi.
        invite.contract_type === 'BIDIRECTIONAL' ? (
          <Tag color="blue">{directionLabel('BIDIRECTIONAL')}</Tag>
        ) : (
          <Tag>{directionLabel(directionOfInvite(invite.contract_type, invite.direction))}</Tag>
        ),
    },
    {
      title: T('Created'),
      key: 'created',
      render: (_: unknown, invite: InviteListItem) => (
        <div>
          <Deadline>{invite.created_at ? new Date(invite.created_at).toLocaleString() : '—'}</Deadline>
          {/* Só aparece no que eu enviei: no recebido é pessoa do parceiro, e o
              servidor não manda. */}
          {invite.created_by && <CellSub>{invite.created_by}</CellSub>}
        </div>
      ),
    },
    {
      title: T('Deadline'),
      key: 'deadline',
      render: (_: unknown, invite: InviteListItem) => (
        <Deadline>{inviteDeadline(invite, now)}</Deadline>
      ),
    },
    {
      title: T('Status'),
      key: 'status',
      render: (_: unknown, invite: InviteListItem) => {
        const style = INVITE_STATUS_STYLE[invite.status];

        return (
          <StatusBadge
            tone={{
              bg: style?.bg || '#eef0f2',
              fg: style?.fg || '#6b7280',
              dot: style?.dot || '#9aa3af',
            }}
          >
            {inviteStatusLabel(invite.status)}
          </StatusBadge>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, invite: InviteListItem) => {
        /*
          A acao da linha nao pode abrir o detalhe tambem.
          A linha inteira abre o painel, e o clique no botao sobe por ela: sem parar a
          propagacao, revogar abria a confirmacao E o painel por tras dela.
        */
        const only = (action: () => void) => (event: React.MouseEvent) => {
          event.stopPropagation();
          action();
        };

        return (
          <Actions>
            {canReview(invite) && (
              <Button type="link" onClick={only(() => onReview(invite.partner_tenant_id))}>
                {T('Review invite')}
              </Button>
            )}

            {canRevoke(invite) && (
              <Button
                type="link"
                danger
                loading={busyId === invite.invite_id}
                onClick={only(() => confirmRevoke(invite))}
              >
                {T('Revoke')}
              </Button>
            )}
          </Actions>
        );
      },
    },
    {
      /* A seta que avisa que a linha abre detalhe, como Conexoes e API. */
      title: '',
      key: 'open',
      width: 40,
      render: () => (
        <Chevron>
          <RightOutlined />
        </Chevron>
      ),
    },
  ];

  if (loadError) {
    return (
      <Result
        status="warning"
        title={T('Could not load the invites.')}
        extra={
          <Button type="primary" onClick={load}>
            {T('Try again')}
          </Button>
        }
      />
    );
  }

  if (loading) {
    return <ListSkeleton />;
  }

  return (
    <ClickableRows>
      <FilterBar>
        <Select
          value={query.direction || ''}
          style={{ minWidth: 170 }}
          onChange={(direction: InviteDirection | '') =>
            changeQuery({ direction: direction || undefined })}
        >
          <Select.Option value="">{T('Both directions')}</Select.Option>
          <Select.Option value="SENT">{T('Sent')}</Select.Option>
          <Select.Option value="RECEIVED">{T('Received')}</Select.Option>
        </Select>

        <Select
          value={query.status || ''}
          style={{ minWidth: 180 }}
          onChange={(status: InviteStatus | '') => changeQuery({ status: status || undefined })}
        >
          <Select.Option value="">{T('All states')}</Select.Option>
          <Select.Option value="VALID">{T('Pending')}</Select.Option>
          <Select.Option value="USED">{T('Accepted')}</Select.Option>
          <Select.Option value="EXPIRED">{T('Expired')}</Select.Option>
          <Select.Option value="REVOKED">{T('Revoked')}</Select.Option>
        </Select>

        <FilterSpacer />

        {/* O total vem do servidor: com paginação lá, é ele quem sabe quantos casaram. */}
        <FilterCount>
          {formatCount(totalElements)} {T('invites')}
        </FilterCount>
      </FilterBar>

    <ListCard>
      <Table
        rowKey="invite_id"
        locale={{
          // Nenhum resultado por filtro é diferente de nenhum convite: quem filtrou precisa
          // saber que existe convite fora do filtro.
          emptyText: Object.keys(query).length
            ? T('No invite matches these filters.')
            : T('No invites yet. Invite a partner to get started.'),
        }}
        pagination={false}
        columns={columns}
        dataSource={invites}
        scroll={{ x: true }}
        rowClassName="interop-row"
        onRow={(invite: InviteListItem) => ({
          onClick: () => setDetail(invite),
        })}
      />

      <Foot>
        <Note>{T('Only whoever sent an invite can revoke it.')}</Note>
        <span>
          {formatCount(totalElements)} {T('invites')}
        </span>
      </Foot>

      {/* Paginador do dashboard; ele e 1-based e a API 0-based. */}
      <Pagination
        currentPage={page + 1}
        pageSize={PAGE_SIZE}
        totalCount={totalElements}
        onPageChange={(next: number) => setPage(next - 1)}
      />
    </ListCard>

    <Drawer
      width={420}
      visible={detail !== null}
      title={detail ? detail.partner_name || T('Invite') : ''}
      onClose={() => setDetail(null)}
    >
      {detail && (
        <InviteDrawerDetails
          invite={detail}
          busy={busyId === detail.invite_id}
          onDelete={() => confirmDelete(detail)}
        />
      )}
    </Drawer>
    </ClickableRows>
  );
};
